import React, { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import api, { resolveCoverUrl } from '../../services/api';
import BookCard from '../../components/BookCard';
import styles from './CategoryBooks.module.css';

const CategoryBooks = () => {
  const { categoryId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const initialName = useMemo(() => {
    const rawName = location.state?.categoryName;
    return typeof rawName === 'string' ? rawName : '';
  }, [location.state]);

  const [categoryName, setCategoryName] = useState(initialName);
  const [books, setBooks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;

    const fetchCategoryBooks = async () => {
      if (!categoryId) {
        setError('未找到分类信息');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError('');
        const { data } = await api.get(`/categories/${categoryId}`);
        if (cancelled) {
          return;
        }

        const resolvedName = data?.name || initialName;
        setCategoryName(resolvedName);

        if (Array.isArray(data?.novels)) {
          const normalized = data.novels
            .map((novel) => {
              const order =
                typeof novel.feature_order === 'number'
                  ? novel.feature_order
                  : Number.MAX_SAFE_INTEGER;
              return {
                id: novel.id,
                title: novel.title,
                author: novel.author,
                description: novel.description,
                cover_image_url: resolveCoverUrl(novel.cover_image_url || novel.cover_img_url),
                category: resolvedName,
                is_completed: novel.is_completed ?? false,
                order,
              };
            })
            .sort((a, b) => {
              if (a.order !== b.order) {
                return a.order - b.order;
              }
              return (a.id || 0) - (b.id || 0);
            })
            .map(({ order, ...rest }) => rest);

          setBooks(normalized);
        } else {
          setBooks([]);
        }
      } catch (err) {
        console.error('获取分类小说失败:', err);
        if (!cancelled) {
          setError('分类小说加载失败，请稍后重试');
          setBooks([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    fetchCategoryBooks();

    return () => {
      cancelled = true;
    };
  }, [categoryId, initialName]);

  const handleBack = () => {
    console.log('[CategoryBooks] handleBack called');
    console.log('[CategoryBooks] current path:', window.location.href);
    console.log('[CategoryBooks] history.length:', window.history.length);
    console.log('[CategoryBooks] location.state:', location.state);
    console.log('[CategoryBooks] calling navigate(-1)');
    navigate(-1);
  };

  const renderContent = () => {
    if (loading) {
      return (
        <div className={styles.statusMessage}>
          正在加载该分类的小说...
        </div>
      );
    }

    if (error) {
      return (
        <div className={`${styles.statusMessage} ${styles.errorText}`}>
          {error}
        </div>
      );
    }

    if (books.length === 0) {
      return (
        <div className={styles.statusMessage}>
          该分类暂时没有小说
        </div>
      );
    }

    return (
      <div className={styles.scrollArea}>
        <div className={styles.booksGrid}>
          {books.map((book) => (
            <BookCard
              key={book.id}
              book={book}
              showStatus
              from={`/categories/${categoryId}`}
            />
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className={styles.categoryPage}>
      <div className={styles.header}>
        <button
          type="button"
          className={styles.backBtn}
          onClick={handleBack}
          aria-label="返回上一页"
        >
          <span className={`material-symbols-outlined ${styles.backIcon}`}>arrow_back</span>
        </button>
        <div className={styles.headerInfo}>
          <h1 className={styles.title}>{categoryName || '分类详情'}</h1>
          {!loading && !error && (
            <span className={styles.count}>
              共 {books.length} 本小说
            </span>
          )}
        </div>
      </div>
      <div className={styles.content}>{renderContent()}</div>
    </div>
  );
};

export default CategoryBooks;
